'use client'

import { FileText } from "lucide-react"
import { navigateUrl } from "@/utils/client-request"
import { usePathname } from "next/navigation"
import EmptyState from "../elements/EmptyState"

const PAGES = [
  { key: 'delivery', href: '/delivery', label: 'Хүргэлтийн нөхцөл' },
  { key: 'payment', href: '/payment', label: 'Төлбөрийн нөхцөл' },
  { key: 'service', href: '/service', label: 'Үйлчилгээний нөхцөл' },
] as const

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const sweepLine = (active: boolean) => `pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${active ? 'bg-size-[100%_1px]' : 'bg-size-[0%_1px] group-hover:bg-size-[100%_1px]'}`

type DocPageProps = {
  vendor?: any
  pageKey: 'delivery' | 'payment' | 'service'
  eyebrow?: string
  titleLead: string
  titleAccent: string
  description?: string
  htmlContent?: string | null
  emptyMessage: string
}

export default function DocPage({ pageKey, titleLead, titleAccent, htmlContent, emptyMessage }: DocPageProps) {
  const pathname = usePathname()

  return (
    <div className="w-full px-4 lg:px-6 pt-12 lg:pt-20 pb-20 lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-20">
      <aside className="hidden lg:block lg:sticky lg:top-24 lg:self-start">
        <div className={eyebrow}>Нөхцөл</div>
        <ul className="mt-4 space-y-1">
          {PAGES.map(p => {
            const active = p.key === pageKey
            return (
              <li key={p.key}>
                <a href={navigateUrl(p.href, pathname)} aria-current={active ? 'page' : undefined} className={`group inline-block py-1.5 text-[15px] transition-colors duration-150 ${active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'} ${focusRing}`}>
                  <span className={sweepLine(active)}>{p.label}</span>
                </a>
              </li>
            )
          })}
        </ul>
      </aside>

      <div className="min-w-0 max-w-3xl">
        <h1 className="text-3xl sm:text-4xl tracking-tight text-foreground">{titleLead} {titleAccent}</h1>
        {htmlContent ? (
          <article
            className="mt-10 prose prose-neutral max-w-none text-foreground prose-headings:font-normal prose-headings:tracking-tight prose-headings:text-foreground prose-h2:text-xl sm:prose-h2:text-2xl prose-h2:mt-14 prose-h2:mb-6 prose-h3:text-lg prose-p:text-[15px] prose-p:leading-loose prose-p:text-foreground/85 prose-li:text-[15px] prose-li:text-foreground/85 prose-li:my-1 prose-strong:text-foreground prose-strong:font-medium prose-a:text-foreground prose-a:underline-offset-4 prose-a:decoration-1 prose-img:w-full prose-img:rounded-none prose-hr:border-border"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : (
          <div className="mt-10">
            <EmptyState icon={FileText} title="Мэдээлэл нэмэгдээгүй байна" description={emptyMessage} />
          </div>
        )}

        <nav aria-label="Бусад нөхцөл" className="lg:hidden mt-16 pt-8 border-t border-border">
          <div className={eyebrow}>Бусад нөхцөл</div>
          <ul className="mt-4 space-y-2">
            {PAGES.filter(p => p.key !== pageKey).map(p => (
              <li key={p.key}>
                <a href={navigateUrl(p.href, pathname)} className={`group inline-block text-[15px] ${focusRing}`}>
                  <span className={sweepLine(false)}>{p.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  )
}
